import React, { useMemo, useState } from "react";
import axios from "axios";
import { PageHeader, Card, StatusPill } from "../components/ui-bj/Primitives";
import { Loading, ErrorState } from "../components/ui-bj/States";
import { useAirtable } from "../hooks/useAirtable";
import { API } from "../context/AuthContext";
import { str, fmtDateTime, timeAgo } from "../lib/airtableHelpers";
import ExportButtons from "../components/ui-bj/ExportButtons";
import PeriodScopeBar, { usePeriodFilter } from "../components/ui-bj/PeriodScopeBar";
import { Envelope, Phone, MagnifyingGlass, ArrowRight, CheckCircle } from "@phosphor-icons/react";

const TOKEN_KEY = "bj_admin_token";
const tokenCfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ""}` } });

const STATUS_ORDER = ["New", "Contacted", "Booked", "Converted", "Cold"];

const LeadsPage = () => {
  const leads = useAirtable("leads");
  const [statusFilter, setStatusFilter] = useState("All");
  const [query, setQuery] = useState("");
  const [convertingId, setConvertingId] = useState(null);
  const [banner, setBanner] = useState(null);       // { kind: 'ok'|'err', text }
  const [convertedIds, setConvertedIds] = useState(new Set());
  const { period, setPeriod, scope, setScope, filterByDate } = usePeriodFilter("all");

  const convert = async (row) => {
    if (!window.confirm(`Convert "${row.name}" to a client?\n\nThis will:\n• Mark the lead as Converted\n• Create a new CLIENTS record\n• Auto-generate a Financial Profile Development starter invoice ($199 setup + 3% fee)\n• Log an audit event`)) return;
    setConvertingId(row.id);
    setBanner(null);
    try {
      const { data } = await axios.post(`${API}/leads/${row.id}/convert`, { starter_invoice: true }, tokenCfg());
      const invoiceLabel = data.invoice ? `Invoice ${data.invoice.number} — ${(data.invoice.totals.total_cents / 100).toLocaleString(undefined, { style: "currency", currency: "USD" })}` : "no starter invoice";
      const warn = (data.warnings || []).length ? ` (${data.warnings.join("; ")})` : "";
      setBanner({ kind: "ok", text: `${row.name} converted → Client ${data.client.id.slice(0, 8)}… · ${invoiceLabel}.${warn}` });
      setConvertedIds((prev) => new Set(prev).add(row.id));
      // Refetch after a short delay so the Airtable cache serves the new record
      setTimeout(() => leads.refetch(), 800);
    } catch (e) {
      setBanner({ kind: "err", text: e?.response?.data?.detail || e.message || "Conversion failed" });
    } finally {
      setConvertingId(null);
    }
  };

  const rows = useMemo(() => (
    (leads.records || [])
      .map((r) => {
        const f = r.fields || {};
        return {
          id: r.id,
          name: str(f["name"]),
          email: str(f["email"]),
          phone: str(f["phone"]),
          source: str(f["source"]),
          creditRange: str(f["credit_range"]),
          businessStage: str(f["business_stage"]),
          monthlyRevenue: str(f["monthly_revenue"]),
          fundingGoal: str(f["funding_goal"]),
          approvalRange: str(f["approval_range"]),
          status: str(f["status"]),
          timestamp: f["Timestamp"],
        };
      })
      .sort((a, b) => (b.timestamp || "").localeCompare(a.timestamp || ""))
  ), [leads.records]);

  const statusCounts = useMemo(() => {
    const counts = { All: rows.length };
    STATUS_ORDER.forEach((s) => { counts[s] = 0; });
    rows.forEach((r) => { if (counts[r.status] !== undefined) counts[r.status] += 1; });
    return counts;
  }, [rows]);

  // Period filter scopes Leads to the createdTime "Timestamp" column.
  const visible = rows.filter((r) => {
    if (!filterByDate(r.timestamp)) return false;
    if (statusFilter !== "All" && r.status !== statusFilter) return false;
    if (query) {
      const blob = `${r.name} ${r.email} ${r.phone} ${r.source}`.toLowerCase();
      if (!blob.includes(query.toLowerCase())) return false;
    }
    return true;
  });

  if (leads.loading) return <Loading label="Loading lead pipeline" />;
  if (leads.error) return <ErrorState message={leads.error} onRetry={leads.refetch} />;

  return (
    <div data-testid="leads-page">
      <PageHeader
        eyebrow="DASHBOARD / LEAD PIPELINE"
        title="Lead Pipeline"
        subtitle={`${visible.length} of ${rows.length} leads • ${scope === "period" ? "filtered by Timestamp" : "all-time view"} • Newest first.`}
        right={
          <div className="flex items-center gap-3 flex-wrap">
            <PeriodScopeBar scope={scope} setScope={setScope} period={period} setPeriod={setPeriod} testidPrefix="leads-scope" />
            <ExportButtons
              title="Lead Pipeline"
              filename="leads"
              testidPrefix="export-leads"
              columns={[
                { key: "timestamp", label: "Timestamp" },
                { key: "name", label: "Name" },
                { key: "email", label: "Email" },
                { key: "phone", label: "Phone" },
                { key: "source", label: "Source" },
                { key: "status", label: "Status" },
                { key: "creditRange", label: "Score Range" },
                { key: "businessStage", label: "Business Stage" },
                { key: "monthlyRevenue", label: "Monthly Revenue" },
                { key: "fundingGoal", label: "Capital goal" },
                { key: "approvalRange", label: "Approval Range" },
              ]}
              rows={visible}
            />
          </div>
        }
      />

      {banner && (
        <div
          data-testid="leads-banner"
          className={`mb-4 px-4 py-3 border text-sm ${banner.kind === "ok" ? "border-[#14281E]/40 text-[#14281E]" : "border-[#5C1A24]/40 text-[#5C1A24]"}`}
        >
          {banner.text}
          <button onClick={() => setBanner(null)} className="ml-3 text-xs underline">dismiss</button>
        </div>
      )}

      <Card className="mb-5">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5E4D40]" />
            <input
              data-testid="leads-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, phone, source…"
              className="w-full pl-9 pr-3 py-2 bg-[#DCCFB9] border border-[rgba(42,28,21,0.18)] text-[#2A1C15] text-sm font-mono focus:border-[#C8A46A] focus:outline-none"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="bj-label mr-2">Status</span>
            {["All", ...STATUS_ORDER].map((s) => (
              <button
                key={s}
                data-testid={`leads-status-${s.toLowerCase()}`}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 border font-heading tracking-widest text-[0.75rem] uppercase transition-colors ${statusFilter === s ? "border-[#2A1C15] bg-[#2A1C15] text-[#DCCFB9]" : "border-[rgba(42,28,21,0.18)] text-[#2A1C15] hover:border-[#2A1C15]"}`}
              >
                {s} <span className="bj-tnum ml-1">({statusCounts[s] ?? 0})</span>
              </button>
            ))}
          </div>
        </div>
      </Card>

      <Card className="overflow-x-auto">
        <table className="w-full min-w-[1100px]">
          <thead>
            <tr className="text-left">
              {["When", "Lead", "Contact", "Source", "Profile", "Capital goal", "Status", "Action"].map((h) => (
                <th key={h} className="bj-table-header py-2 pr-3 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan={8} className="py-10 text-center text-[#5E4D40] text-sm">No leads for this filter.</td></tr>
            )}
            {visible.map((r, i) => (
              <tr key={r.id} data-testid={`lead-row-${i}`} className="bj-table-row">
                <td className="py-3 pr-3 text-sm whitespace-nowrap">
                  <div className="text-[#2A1C15] bj-tnum">{fmtDateTime(r.timestamp)}</div>
                  <div className="text-[#5E4D40] text-[0.75rem] tracking-widest uppercase font-heading">{timeAgo(r.timestamp)}</div>
                </td>
                <td className="py-3 pr-3 text-[#6E5022] text-sm font-medium">{r.name}</td>
                <td className="py-3 pr-3 text-xs">
                  {r.email !== "—" && (
                    <a href={`mailto:${r.email}`} className="flex items-center gap-1.5 text-[#2A1C15] hover:text-[#6E5022]" data-testid={`lead-email-${i}`}>
                      <Envelope size={12} /> <span className="bj-tnum">{r.email}</span>
                    </a>
                  )}
                  {r.phone !== "—" && (
                    <a href={`tel:${r.phone}`} className="flex items-center gap-1.5 text-[#5E4D40] hover:text-[#6E5022] mt-1" data-testid={`lead-phone-${i}`}>
                      <Phone size={12} /> <span className="bj-tnum">{r.phone}</span>
                    </a>
                  )}
                </td>
                <td className="py-3 pr-3 text-[#5E4D40] text-sm">{r.source}</td>
                <td className="py-3 pr-3 text-xs">
                  <div className="text-[#2A1C15]">Score · <span className="bj-tnum">{r.creditRange}</span></div>
                  <div className="text-[#5E4D40]">Stage · {r.businessStage}</div>
                  <div className="text-[#5E4D40]">Revenue · <span className="bj-tnum">{r.monthlyRevenue}</span></div>
                </td>
                <td className="py-3 pr-3 text-xs">
                  <div className="text-[#6E5022] bj-tnum">{r.fundingGoal}</div>
                  <div className="text-[#5E4D40]">Approval · <span className="bj-tnum">{r.approvalRange}</span></div>
                </td>
                <td className="py-3 pr-3">{r.status !== "—" ? <StatusPill status={r.status} /> : <span className="text-[#5E4D40] text-sm">—</span>}</td>
                <td className="py-3 pr-3">
                  {(r.status === "Converted" || convertedIds.has(r.id)) ? (
                    <span data-testid={`lead-converted-${i}`} className="inline-flex items-center gap-1 text-[#14281E] text-xs font-heading tracking-widest uppercase">
                      <CheckCircle size={14} weight="fill" /> Converted
                    </span>
                  ) : (
                    <button
                      data-testid={`lead-convert-${i}`}
                      onClick={() => convert(r)}
                      disabled={convertingId === r.id}
                      className="inline-flex items-center gap-1 px-2.5 py-1 border border-[#C8A46A] text-[#6E5022] hover:bg-[#2A1C15] hover:text-[#DCCFB9] font-heading tracking-widest text-[0.75rem] uppercase transition-colors disabled:opacity-60"
                    >
                      {convertingId === r.id ? "Converting…" : <>Convert <ArrowRight size={12} weight="bold" /></>}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

export default LeadsPage;
